import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import Auth from '@aws-amplify/auth';

import { CHAT_SEND_MESSAGE } from '../queries';
import { isConfigured as isStreamConfigured, subscribe } from '../lib/appsyncEvents';

// A spread across the warehouse - catalogue, sales, availability, licensors
// and content ops - so the empty state doubles as a map of what she can be asked.
// All are self-contained: a suggestion that needs a follow-up ("in a given
// accounting period") answers with a question instead of data, which is a poor
// first impression of the assistant.
export const SUGGESTIONS = [
    'How many active audiobooks are in the catalogue, by language?',
    'Which active titles are available in Canada but not the United States?',
    'What were the 10 best-selling audiobooks in the latest accounting period?',
    'How did unit sales change between the two latest accounting periods?',
    'Which publisher ranked highest by revenue in the latest accounting period?',
    'Which products have a sale window ending in the next 30 days?',
    'Which active licensors do not have a royalty contract?',
    'Which delivered audio files are missing technical metadata?'
];

// Same reasoning as the waiting lines in ChatTranscript: a request that fails
// once tends to fail a few times in a row, and the same sentence three deep in a
// transcript reads worse each time. Every one of them still ends by asking for a
// retry - the joke is the wrapper, not the instruction.
const FAILURES = [
    'BigBobby hung up on me. Give it another go?',
    'I reached for that one and came back empty-handed. Try again?',
    'That question fell behind the shelves somewhere. Ask again?',
    'Redshift is not taking my calls right now. One more try?',
    'Something snapped between here and the catalogue. Try again?'
];

// History lives in localStorage and nowhere else. The agent keeps its own
// conversation state keyed by conversationId, so what is stored here is only
// the browser's record of what was said and which threads exist - clearing it
// loses the transcripts, not the agent's memory.
const HISTORY_LIMIT = 30;
const TITLE_LIMIT = 60;

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const newConversation = () => {
    const now = Date.now();
    return { id: makeId(), title: '', createdAt: now, updatedAt: now, messages: [] };
};

// The opening question names the thread. There is no rename, so this is the
// only label a conversation ever carries.
const titleFrom = (text) => {
    const line = (text || '').trim().split('\n')[0].trim();
    if (!line) {
        return 'New convo';
    }
    return line.length > TITLE_LIMIT ? `${line.slice(0, TITLE_LIMIT - 1)}…` : line;
};

const appendMessage = (state, conversationId, message) => ({
    ...state,
    conversations: state.conversations.map((conversation) => {
        if (conversation.id !== conversationId) {
            return conversation;
        }
        return {
            ...conversation,
            title: conversation.title || (message.role === 'user' ? titleFrom(message.body) : ''),
            updatedAt: Date.now(),
            messages: [...conversation.messages, message]
        };
    })
});

// One store per storage key, shared by every useChat instance rather than held
// per hook. The widget stays mounted underneath the full page, so two copies of
// this state would drift apart and then write the stale one back over the good.
const stores = new Map();

const createStore = (storageKey) => {
    const historyKey = `${storageKey}.history`;
    const legacyIdKey = `${storageKey}.conversationId`;
    const legacyMessagesKey = `${storageKey}.messages`;

    const readJson = (key) => {
        try {
            const raw = window.localStorage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    };

    // Whatever was stored before history existed is folded in as the first
    // thread rather than dropped, so upgrading does not discard the open one.
    const migrateLegacy = () => {
        const id = readJson(legacyIdKey);
        const messages = readJson(legacyMessagesKey);

        if (!id || !Array.isArray(messages) || !messages.length) {
            return null;
        }

        const now = Date.now();
        const opening = messages.find((message) => message.role === 'user');
        return {
            currentId: id,
            conversations: [
                { id, title: titleFrom(opening && opening.body), createdAt: now, updatedAt: now, messages }
            ]
        };
    };

    const load = () => {
        const stored = readJson(historyKey);
        if (stored && Array.isArray(stored.conversations) && stored.conversations.length) {
            return { currentId: stored.currentId, conversations: stored.conversations };
        }

        const migrated = migrateLegacy();
        if (migrated) {
            return migrated;
        }

        const fresh = newConversation();
        return { currentId: fresh.id, conversations: [fresh] };
    };

    // An empty thread is not history: starting a new conversation and switching
    // away should leave nothing behind. Newest first, since that is the order
    // both surfaces list them in.
    const prune = (state) => {
        const conversations = state.conversations
            .filter((conversation) => conversation.messages.length || conversation.id === state.currentId)
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .slice(0, HISTORY_LIMIT);

        if (!conversations.length) {
            const fresh = newConversation();
            return { currentId: fresh.id, conversations: [fresh] };
        }
        if (!conversations.some((conversation) => conversation.id === state.currentId)) {
            return { currentId: conversations[0].id, conversations };
        }
        return { currentId: state.currentId, conversations };
    };

    // Transcripts carry whole result tables, so a long history can outgrow the
    // ~5MB budget. Drop the oldest threads until the write fits rather than
    // letting the quota error lose the turn that was just sent.
    const persist = (state) => {
        let conversations = state.conversations;

        while (conversations.length) {
            try {
                window.localStorage.setItem(
                    historyKey,
                    JSON.stringify({ currentId: state.currentId, conversations })
                );
                return conversations;
            } catch (error) {
                if (conversations.length === 1) {
                    return conversations;
                }
                conversations = conversations.slice(0, -1);
            }
        }

        return conversations;
    };

    let state = prune(load());

    // The turn currently arriving, held apart from `state` because it is not
    // history yet and must not be written to localStorage. Persisting on every
    // token would re-serialise the whole transcript tens of times a second;
    // the answer joins the conversation once, when it is complete.
    let stream = null;

    const listeners = new Set();
    const snapshot = () => ({ ...state, stream });

    // The legacy keys are dropped only once their contents are safely written
    // under the new one. Deleting them at read time would lose the thread if the
    // tab closed before the first message forced a write.
    if (readJson(legacyIdKey) !== null || readJson(legacyMessagesKey) !== null) {
        state = { currentId: state.currentId, conversations: persist(state) };
        try {
            window.localStorage.removeItem(legacyIdKey);
            window.localStorage.removeItem(legacyMessagesKey);
        } catch (error) {
            // Worst case the old keys linger, unread.
        }
    }

    const notify = () => {
        const current = snapshot();
        listeners.forEach((listener) => listener(current));
    };

    const setState = (updater) => {
        const next = prune(typeof updater === 'function' ? updater(state) : updater);
        state = { currentId: next.currentId, conversations: persist(next) };
        notify();
    };

    const setStream = (updater) => {
        stream = typeof updater === 'function' ? updater(stream) : updater;
        notify();
    };

    // Another tab editing the same history should show up here rather than be
    // silently overwritten by whatever this one happens to write next.
    window.addEventListener('storage', (event) => {
        if (event.key !== historyKey) {
            return;
        }
        state = prune(load());
        notify();
    });

    return {
        getState: snapshot,
        setState,
        setStream,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
};

const getStore = (storageKey) => {
    if (!stores.has(storageKey)) {
        stores.set(storageKey, createStore(storageKey));
    }
    return stores.get(storageKey);
};

// What the agent is doing right now, in the user's terms. A 40-second answer
// with a line that changes as the work moves reads as progress; the same
// 40 seconds behind one static spinner reads as a hang.
const TOOL_STATUS = {
    execute_query: 'Querying BigBobby…',
    list_workgroups: 'Finding BigBobby…',
    list_schemas: "Rifling through BigBobby's schemas…",
    list_tables: "Flipping through BigBobby's tables…",
    list_columns: "Squinting at BigBobby's columns…"
};

const statusFor = (name) => TOOL_STATUS[name] || 'Digging through the catalogue…';

const channelFor = (sub, conversationId) => `/chat/${sub}/${conversationId}`;

// The channel has to be known before the mutation is sent - the Events API has
// no replay, so subscribing afterwards loses whatever already went out. That
// means deriving the sub here rather than reading it off the mutation's reply.
const currentSub = async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().payload.sub;
};

// Events are published in batches and their delivery order is not guaranteed,
// so they carry a sequence number and are replayed in it. A gap that does not
// close is a genuinely lost event: waiting on it forever would strand the
// answer, so the buffer gives up after a moment and carries on with a hole.
const GAP_TIMEOUT_MS = 2000;

const createOrderedReader = (apply) => {
    let next = 0;
    let timer = null;
    const pending = new Map();

    const drain = () => {
        while (pending.has(next)) {
            const event = pending.get(next);
            pending.delete(next);
            next += 1;
            apply(event);
        }
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        if (pending.size) {
            timer = setTimeout(() => {
                // Skip to whatever did arrive rather than stall behind what did not.
                next = Math.min(...pending.keys());
                drain();
            }, GAP_TIMEOUT_MS);
        }
    };

    return {
        push: (event) => {
            const seq = typeof event.seq === 'number' ? event.seq : next;
            if (seq < next) {
                return; // already applied, or a duplicate delivery
            }
            pending.set(seq, event);
            drain();
        },
        stop: () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        }
    };
};

/**
 * Conversation state, history and sending, shared by the popup and the full
 * page.
 *
 * `storageKey` scopes the local-storage entry, so the two surfaces can either
 * share a thread and its history (same key) or hold separate ones.
 */
export const useChat = (storageKey = 'aegateway.chat') => {
    const store = getStore(storageKey);
    const [state, setState] = useState(store.getState);

    // Re-read on subscribe as well: anything the store recorded between this
    // component's first render and its effects running would otherwise be missed.
    useEffect(() => {
        setState(store.getState());
        return store.subscribe(setState);
    }, [store]);

    const [sendMessage] = useMutation(CHAT_SEND_MESSAGE);

    const open = state.conversations.find((conversation) => conversation.id === state.currentId);
    const messages = open ? open.messages : [];
    const stream = state.stream;
    // A response can keep arriving after the user browses to another thread.
    // Only its owning transcript is "loading"; `busy` remains global because
    // this store deliberately supports one in-flight response at a time.
    const currentStream = stream && stream.conversationId === state.currentId ? stream : null;
    const loading = Boolean(currentStream);
    const busy = Boolean(stream);

    const send = useCallback(
        async (text) => {
            const question = (text || '').trim();
            if (!question || store.getState().stream) {
                return;
            }

            // Resolved once, at send time: the answer belongs to the thread that
            // asked, even if the user switches conversations while it is in flight.
            const targetId = store.getState().currentId;

            store.setState((previous) => appendMessage(previous, targetId, { role: 'user', body: question }));

            const finish = (body, isError) => {
                store.setStream(null);
                store.setState((previous) =>
                    appendMessage(previous, targetId, { role: 'assistant', body, isError: Boolean(isError) })
                );
            };

            if (!isStreamConfigured()) {
                finish(
                    'Chat streaming is not configured for this environment, so answers have nowhere to arrive.',
                    true
                );
                return;
            }

            store.setStream({ conversationId: targetId, text: '', status: 'Waking up BigBobby…' });

            let unsubscribe = null;
            let reader = null;
            let settled = false;

            // `done` always arrives last, so the transcript is committed there
            // rather than on the closing text. An error before it still has to
            // release the composer, which is why this is idempotent.
            const settle = (body, isError) => {
                if (settled) {
                    return;
                }
                settled = true;
                if (reader) {
                    reader.stop();
                }
                if (unsubscribe) {
                    unsubscribe();
                }
                finish(body, isError);
            };

            let answer = '';
            let failure = null;

            reader = createOrderedReader((event) => {
                switch (event.t) {
                    case 'delta':
                        answer += event.text || '';
                        store.setStream((previous) =>
                            previous && previous.conversationId === targetId
                                ? { ...previous, text: answer, status: null }
                                : previous
                        );
                        break;

                    case 'tool':
                        store.setStream((previous) =>
                            previous && previous.conversationId === targetId
                                ? { ...previous, status: statusFor(event.name) }
                                : previous
                        );
                        break;

                    case 'error':
                        // Held rather than shown immediately: the agent can
                        // fail after saying something useful, and replacing a
                        // partial answer with the error loses both.
                        failure = event.message || 'Something went wrong answering that.';
                        break;

                    case 'done':
                        if (answer.trim()) {
                            settle(failure ? `${answer.trim()}\n\n${failure}` : answer.trim(), Boolean(failure));
                        } else {
                            settle(failure || 'No answer was returned.', true);
                        }
                        break;

                    default:
                        break;
                }
            });

            try {
                const sub = await currentSub();
                const channel = channelFor(sub, targetId);

                // Subscribe first. The Events API does not replay, so a
                // question sent before the socket is live loses its opening.
                unsubscribe = await subscribe(channel, (event) => reader.push(event));

                const { data } = await sendMessage({
                    variables: { message: question, conversationId: targetId }
                });
                const reply = data?.chatSendMessage;

                if (reply?.error) {
                    settle(reply.error, true);
                    return;
                }
                if (reply?.channel && reply.channel !== channel) {
                    // The worker publishes where the resolver said it would, so
                    // a mismatch means this client would listen to silence.
                    console.error(`Chat channel mismatch: expected ${channel}, got ${reply.channel}`);
                    settle(FAILURES[Math.floor(Math.random() * FAILURES.length)], true);
                }
            } catch (error) {
                console.error(error);
                settle(FAILURES[Math.floor(Math.random() * FAILURES.length)], true);
            }
        },
        [sendMessage, store]
    );

    const startNew = useCallback(() => {
        store.setState((previous) => {
            const fresh = newConversation();
            return { currentId: fresh.id, conversations: [fresh, ...previous.conversations] };
        });
    }, [store]);

    const selectConversation = useCallback(
        (id) => {
            store.setState((previous) =>
                previous.conversations.some((conversation) => conversation.id === id)
                    ? { ...previous, currentId: id }
                    : previous
            );
        },
        [store]
    );

    const deleteConversation = useCallback(
        (id) => {
            store.setState((previous) => {
                const conversations = previous.conversations.filter((conversation) => conversation.id !== id);
                if (previous.currentId !== id) {
                    return { ...previous, conversations };
                }
                // Deleting the open thread lands on the next most recent; prune
                // supplies a fresh one when that was the last of them.
                return { currentId: conversations.length ? conversations[0].id : null, conversations };
            });
        },
        [store]
    );

    const clearHistory = useCallback(() => {
        store.setState(() => ({ currentId: null, conversations: [] }));
    }, [store]);

    return {
        messages,
        send,
        loading,
        busy,
        stream: currentStream,
        pendingConversationId: stream ? stream.conversationId : null,
        conversationId: state.currentId,
        // Only threads with something in them are history. The open one joins
        // the list as soon as it has a first message.
        conversations: state.conversations.filter((conversation) => conversation.messages.length),
        startNew,
        selectConversation,
        deleteConversation,
        clearHistory
    };
};

export default useChat;
